import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { NativeSelect } from "@/components/ui/NativeSelect";
import type { SupportTicket, TicketHistoryResponse } from "@/lib/support";
import { replyAttachments, ticketLevelAttachments } from "@/lib/support";
import { SupportAttachments } from "@/pages/support/SupportAttachments";
import {
  createSupportTicket,
  fetchTicketHistory,
  postTicketReply,
  updateTicketMessage,
  updateTicketStatus,
  updateTicketTodo,
} from "@/lib/support";
import { PROGRAM_OPTIONS, STATUS_LABEL, STATUS_OPTIONS, URGENCY_LABEL, URGENCY_USER_HINT } from "@/pages/support/constants";
import { fmtDate, toastErr, toastOk } from "@/pages/support/utils";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";

const TICKET_FILE_EXT = /\.(png|jpe?g|webp|gif|pdf|eml|msg)$/i;
const TICKET_FILE_MAX_BYTES = 10 * 1024 * 1024;

/* ─── Neues Ticket ─── */

export function NewTicketDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [program, setProgram] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setMessage("");
      setUrgency("normal");
      setProgram("");
      setFiles([]);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const t = title.trim();
    const m = message.trim();
    if (t.length < 5) return toastErr("Titel zu kurz");
    if (t.length > 20) return toastErr("Titel darf max. 20 Zeichen haben");
    if (m.length < 10) return toastErr("Beschreibung zu kurz");
    if (!program) return toastErr("Bitte ein Programm wählen");
    if (files.some((f) => !TICKET_FILE_EXT.test(f.name))) {
      return toastErr("Nur Bilder, PDF und E-Mails (.eml, .msg) sind erlaubt");
    }
    if (files.some((f) => f.size > TICKET_FILE_MAX_BYTES)) {
      return toastErr("Datei zu groß (max. 10 MB)");
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.set("title", t);
      fd.set("message", m);
      fd.set("urgency", urgency);
      fd.set("program", program);
      for (const f of files) {
        fd.append("images", f);
      }
      await createSupportTicket(fd);
      toastOk("Ticket erstellt");
      onClose();
      onCreated();
    } catch (err) {
      console.error(err);
      toastErr("Ticket konnte nicht erstellt werden");
    } finally {
      setSaving(false);
    }
  }

  const titleLeft = Math.max(0, 20 - title.trim().length);
  const messageLen = message.trim().length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Neues Support-Ticket"
      size="lg"
      dirty={!!title || !!message || files.length > 0}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="rounded-ios border border-separator/60 bg-fill-quaternary/20 px-3 py-2.5 text-sm text-label-secondary">
          Beschreibe dein Anliegen möglichst konkret – Screenshots, PDFs oder E-Mails helfen, schneller zu helfen.
          Du erhältst Updates per E-Mail und kannst den Fortschritt hier verfolgen.
        </p>

        <div>
          <label className="eh-label flex justify-between">
            <span>Betreff</span>
            <span className="text-xs text-label-tertiary tabular-nums">{titleLeft}/20</span>
          </label>
          <input
            className="eh-input"
            value={title}
            maxLength={20}
            required
            minLength={5}
            placeholder='Kurz und präzise, z. B. „Login funktioniert nicht"'
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="eh-label">Betroffenes Programm</label>
            <NativeSelect value={program} required onChange={(e) => setProgram(e.target.value)}>
              <option value="" disabled>
                Bitte wählen …
              </option>
              {PROGRAM_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label className="eh-label">Dringlichkeit</label>
            <NativeSelect value={urgency} onChange={(e) => setUrgency(e.target.value)}>
              <option value="low">{URGENCY_LABEL.low}</option>
              <option value="normal">{URGENCY_LABEL.normal}</option>
              <option value="high">{URGENCY_LABEL.high}</option>
              <option value="critical">{URGENCY_LABEL.critical}</option>
            </NativeSelect>
            <p className="mt-1 text-xs text-label-tertiary">
              {URGENCY_USER_HINT[urgency] || URGENCY_USER_HINT.normal}
            </p>
          </div>
        </div>

        <div>
          <label className="eh-label flex justify-between">
            <span>Beschreibung</span>
            <span className="text-xs text-label-tertiary tabular-nums">{messageLen}/5000</span>
          </label>
          <textarea
            className="eh-input min-h-[9rem]"
            value={message}
            required
            minLength={10}
            maxLength={5000}
            rows={6}
            placeholder={
              "Was ist passiert?\nWas hast du erwartet?\nWelche Schritte führen zum Problem?\n(Fehlermeldungen bitte mit angeben)"
            }
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <div>
          <label className="eh-label">Anhänge (optional)</label>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,.pdf,application/pdf,.eml,.msg,message/rfc822"
            multiple
            className="eh-input py-2"
            onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
          />
          <p className="mt-1 text-xs text-label-tertiary">
            Max. 10 MB pro Datei · Bilder (JPG, PNG, GIF, WebP), PDF und E-Mails (.eml, .msg)
          </p>
          {files.length ? (
            <ul className="mt-2 space-y-1 text-sm text-label-secondary">
              {files.map((f) => (
                <li key={`${f.name}-${f.size}-${f.lastModified}`} className="truncate">
                  {f.name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Wird erstellt …" : "Ticket absenden"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/* ─── Status ändern (User) ─── */

export function StatusChangeDialog({
  open,
  ticket,
  onClose,
  onSaved,
}: {
  open: boolean;
  ticket: SupportTicket | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState("open");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState<TicketHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !ticket) return;
    setStatus(String(ticket.status || "open"));
    setNote("");
    setLoading(true);
    void fetchTicketHistory(ticket.id)
      .then(setHistory)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, ticket]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ticket) return;
    setSaving(true);
    try {
      await updateTicketStatus(ticket.id, status, note.trim());
      if (status === "resolved" || status === "closed") {
        await updateTicketTodo(ticket.id, { todo_state: "erledigt" });
      } else if (status === "open" || status === "in_progress") {
        await updateTicketTodo(ticket.id, {
          todo_state: status === "in_progress" ? "in_umsetzung" : "eingang",
        });
      }
      toastOk("Status aktualisiert & Mail versendet");
      onClose();
      onSaved();
    } catch (err) {
      console.error(err);
      toastErr("Status konnte nicht geändert werden");
    } finally {
      setSaving(false);
    }
  }

  if (!ticket) return null;

  return (
    <Dialog open={open} onClose={onClose} title="Status ändern" size="lg" dirty={!!note}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-xs text-label-tertiary">Ticket</p>
          <p className="font-semibold text-label">{ticket.title}</p>
        </div>
        {history?.ticket?.message ? (
          <div className="rounded-ios border border-separator bg-fill-quaternary/30 p-3 text-sm whitespace-pre-wrap">
            {history.ticket.message}
          </div>
        ) : null}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-label-tertiary">Verlauf</p>
          {loading ? (
            <p className="text-sm text-label-secondary">Lädt …</p>
          ) : !history?.history?.length ? (
            <p className="text-sm text-label-tertiary">Noch keine Änderungen protokolliert.</p>
          ) : (
            <div className="max-h-40 overflow-auto rounded-ios border border-separator text-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-separator text-left text-xs text-label-tertiary">
                    <th className="p-2">Zeit</th>
                    <th className="p-2">Wer</th>
                    <th className="p-2">Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {history.history.map((h, i) => (
                    <tr key={i} className="border-b border-separator/50">
                      <td className="p-2 whitespace-nowrap">{fmtDate(h.changed_at)}</td>
                      <td className="p-2">{h.changed_by_display || h.changed_by_login || "?"}</td>
                      <td className="p-2">
                        {STATUS_LABEL[h.old_status || ""] || h.old_status || "—"} →{" "}
                        <strong>{STATUS_LABEL[h.new_status || ""] || h.new_status}</strong>
                        {h.note ? <div className="text-label-secondary">{h.note}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div>
          <label className="eh-label">Neuer Status</label>
          <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label className="eh-label">Hinweis an den Melder (optional)</label>
          <textarea
            className="eh-input"
            rows={4}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="mt-1 text-xs text-label-tertiary">Der Hinweis wird per E-Mail mit versendet.</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            Speichern &amp; Mail senden
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/* ─── Historie (User) ─── */

export function TicketHistoryDialog({
  open,
  ticket,
  isAdmin,
  onClose,
  onSaved,
}: {
  open: boolean;
  ticket: SupportTicket | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useCurrentUser();
  const login = getUserLocalLogin(user).toLowerCase();
  const [data, setData] = useState<TicketHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !ticket) return;
    setReply("");
    setLoading(true);
    void fetchTicketHistory(ticket.id)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, ticket]);

  const merged = data?.ticket ?? ticket;
  const canEdit =
    isAdmin ||
    (String(merged?.status || "") === "open" &&
      String(merged?.reporter_login || "").toLowerCase() === login);
  const isClosed = String(merged?.status || "") === "closed";

  async function sendReply(e: React.FormEvent) {
    e.preventDefault();
    if (!ticket) return;
    const msg = reply.trim();
    if (!msg) return toastErr("Antwort darf nicht leer sein");
    setSaving(true);
    try {
      await postTicketReply(ticket.id, msg);
      setReply("");
      const fresh = await fetchTicketHistory(ticket.id);
      setData(fresh);
      toastOk("Antwort gesendet");
      onSaved();
    } catch (err) {
      console.error(err);
      toastErr("Antwort konnte nicht gesendet werden");
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!ticket) return;
    const msg = editMessage.trim();
    if (msg.length < 10) return toastErr("Beschreibung zu kurz");
    setSaving(true);
    try {
      await updateTicketMessage(ticket.id, msg);
      toastOk("Ticket aktualisiert");
      setEditOpen(false);
      const fresh = await fetchTicketHistory(ticket.id);
      setData(fresh);
      onSaved();
    } catch (err) {
      console.error(err);
      toastErr("Ticket konnte nicht bearbeitet werden");
    } finally {
      setSaving(false);
    }
  }

  if (!ticket) return null;

  return (
    <>
      <Dialog open={open && !editOpen} onClose={onClose} title="Ticket-Historie" size="lg">
        {loading ? (
          <p className="text-sm text-label-secondary">Lädt …</p>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <p className="text-sm text-label-secondary">
              #{ticket.id} · {merged?.title}
            </p>
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-label">Beschreibung</h3>
                {canEdit ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="text-xs"
                    onClick={() => {
                      setEditMessage(String(merged?.message || ""));
                      setEditOpen(true);
                    }}
                  >
                    Bearbeiten
                  </Button>
                ) : null}
              </div>
              <div className="rounded-ios border border-separator bg-fill-quaternary/25 p-3 text-sm whitespace-pre-wrap">
                {merged?.message || "(Keine Beschreibung)"}
              </div>
            </section>
            {ticketLevelAttachments(data?.attachments).length || !(data?.attachments || []).length ? (
              <SupportAttachments
                attachments={ticketLevelAttachments(data?.attachments)}
                variant="list"
                title="Anhänge"
                emptyText="Keine Anhänge vorhanden."
              />
            ) : null}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-label">Verlauf</h3>
              {!data?.history?.length ? (
                <p className="text-sm text-label-tertiary">Noch keine Änderungen.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  {data.history.map((h, i) => (
                    <div key={i} className="rounded-ios border border-separator/60 px-3 py-2">
                      <div className="flex justify-between gap-2 text-xs text-label-tertiary">
                        <span>{h.changed_by_display || h.changed_by_login}</span>
                        <span>{fmtDate(h.changed_at)}</span>
                      </div>
                      <div>
                        {STATUS_LABEL[h.old_status || ""] || h.old_status || "—"} →{" "}
                        <strong>{STATUS_LABEL[h.new_status || ""] || h.new_status}</strong>
                        {h.note ? <div className="mt-1 whitespace-pre-wrap">{h.note}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section>
              <h3 className="mb-2 text-sm font-semibold text-label">Antworten</h3>
              {!data?.replies?.length ? (
                <p className="text-sm text-label-tertiary">Noch keine Antworten.</p>
              ) : (
                <div className="space-y-2">
                  {data.replies.map((r, i) => (
                    <div
                      key={i}
                      className="rounded-ios border border-separator bg-fill-quaternary/20 px-3 py-2 text-sm"
                    >
                      <div className="flex justify-between text-xs text-label-tertiary">
                        <span>{r.author_display || r.author_login}</span>
                        <span>{fmtDate(r.created_at)}</span>
                      </div>
                      {r.message?.trim() ? (
                        <div className="mt-1 whitespace-pre-wrap">{r.message}</div>
                      ) : null}
                      {replyAttachments(data?.attachments, r.id).length ? (
                        <SupportAttachments
                          attachments={replyAttachments(data?.attachments, r.id)}
                          variant="grid"
                          showTitle={false}
                          className="mt-2"
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </section>
            {!isClosed ? (
              <form onSubmit={sendReply} className="space-y-2 border-t border-separator pt-4">
                <label className="eh-label">Neue Antwort</label>
                <textarea
                  className="eh-input"
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  required
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    Antwort senden
                  </Button>
                </div>
              </form>
            ) : null}
          </div>
        )}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {isAdmin && merged && String(merged.status || "") !== "closed" ? (
            <>
              {String(merged.status || "") !== "resolved" ? (
                <Button
                  type="button"
                  className="bg-status-green text-white hover:opacity-90"
                  disabled={saving}
                  onClick={async () => {
                    if (!ticket) return;
                    const raw = window.prompt("Hinweis an den Melder (optional):", "");
                    if (raw === null) return;
                    setSaving(true);
                    try {
                      await updateTicketStatus(ticket.id, "resolved", raw.trim());
                      await updateTicketTodo(ticket.id, { todo_state: "erledigt" });
                      toastOk("Ticket als gelöst markiert");
                      const fresh = await fetchTicketHistory(ticket.id);
                      setData(fresh);
                      onSaved();
                    } catch (err) {
                      console.error(err);
                      toastErr("Konnte Ticket nicht als gelöst markieren");
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Als gelöst markieren
                </Button>
              ) : null}
            </>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Ticket bearbeiten"
        size="lg"
        dirty={editMessage !== String(merged?.message || "")}
      >
        <form onSubmit={saveEdit} className="space-y-4">
          <textarea
            className="eh-input min-h-[10rem]"
            value={editMessage}
            required
            minLength={10}
            onChange={(e) => setEditMessage(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={saving}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

export { STATUS_LABEL };
